import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  Wallet,
  Calendar,
  Building2,
  FileText,
  X,
  Truck,
  Receipt,
  Image as ImageIcon,
  Search,
  Package2,
  Layers,
  Trash2,
} from "lucide-react";
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  IconButton,
  Checkbox,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { dummyProducts } from "../../../dummyData";
import { dummyMaterials } from "../ProductionModule/Components/MaterialComponents/MaterialTable";

// Sample data for dropdowns
const suppliers = [
  { id: 1, name: "Prints Bank" },
  { id: 2, name: "Supplier 1" },
];

const agents = [
  { id: 1, name: "Direct" },
  { id: 2, name: "Agent 1" },
  { id: 3, name: "Agent 2" },
];

const purchaseOrders = [
  { id: "PO-1", supplier: "Prints Bank" },
  { id: "PO-2", supplier: "Supplier 1" },
];

const units = ["meters", "pieces", "kg", "grams", "yards"];

const CreatePurchaseInward = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const editId = params.get("id");

  const [form, setForm] = useState({
    supplier: "",
    agent: "",
    purchaseOrderNo: "",
    challanDate: dayjs(),
    challanNo: "",
    discount: "",
    lrNo: "",
    transport: "",
    note: "",
    challanPhotos: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [tempSelectedItems, setTempSelectedItems] = useState([]);
  const [materialRows, setMaterialRows] = useState({});
  const [productRows, setProductRows] = useState({});
  const [colors, setColors] = useState([
    "Red",
    "Blue",
    "Green",
    "Black",
    "White",
  ]);
  const [sizes, setSizes] = useState(["S", "M", "L", "XL", "XXL"]);
  const [showAddColorModal, setShowAddColorModal] = useState(false);
  const [showAddSizeModal, setShowAddSizeModal] = useState(false);
  const [newColor, setNewColor] = useState("");
  const [newSize, setNewSize] = useState("");
  const [colorError, setColorError] = useState("");
  const [sizeError, setSizeError] = useState("");
  const [itemImages, setItemImages] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const [itemNotes, setItemNotes] = useState({});
  const [currentNote, setCurrentNote] = useState("");

  // Load data if editing
  useEffect(() => {
    if (editId) {
      setForm({
        supplier: "Prints Bank",
        agent: "Direct",
        purchaseOrderNo: "PO-1",
        challanDate: dayjs(),
        challanNo: "CH-2024-001",
        discount: "",
        lrNo: "LR-001",
        transport: "Express Logistics",
        note: "",
        challanPhotos: [],
      });
    }
  }, [editId]);

  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.supplier) newErrors.supplier = "Supplier is required";
    if (!form.purchaseOrderNo)
      newErrors.purchaseOrderNo = "Purchase Order No is required";
    if (!form.challanDate) newErrors.challanDate = "Challan date is required";
    if (!form.challanNo) newErrors.challanNo = "Challan No is required";
    if (
      form.discount &&
      (isNaN(form.discount) ||
        Number(form.discount) < 0 ||
        Number(form.discount) > 100)
    ) {
      newErrors.discount = "Discount must be between 0 and 100";
    }
    if (selectedItems.length === 0) {
      newErrors.items = "At least one item must be added to the order";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        console.log("Form submitted:", form);
        setIsSubmitting(false);
        setShowSuccessModal(true);
      }, 1000);
    }
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirmed = () => {
    setForm({
      supplier: "",
      agent: "",
      purchaseOrderNo: "",
      challanDate: dayjs(),
      challanNo: "",
      discount: "",
      lrNo: "",
      transport: "",
      note: "",
      challanPhotos: [],
    });
    setErrors({});
    setShowCancelDialog(false);
    navigate("/purchase/inward");
  };

  const handleImageUpload = (itemId, event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => URL.createObjectURL(file));
    setItemImages(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newImages],
    }));
  };

  const handleRemoveImage = (itemId, imageIndex) => {
    setItemImages(prev => ({
      ...prev,
      [itemId]: prev[itemId].filter((_, i) => i !== imageIndex),
    }));
  };

  const handleAddItem = () => {
    setShowAddItemModal(true);
    setSelectedTab(0);
    setSearchQuery("");
    setTempSelectedItems([]);
  };

  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue);
    setSearchQuery("");
  };

  const filteredItems = (
    selectedTab === 0 ? dummyProducts : dummyMaterials
  ).filter(item => {
    const searchLower = searchQuery.toLowerCase();
    if (selectedTab === 0) {
      return (
        item.name.toLowerCase().includes(searchLower) ||
        item.brand.name.toLowerCase().includes(searchLower) ||
        item.productId.toLowerCase().includes(searchLower)
      );
    } else {
      return (
        item.name.toLowerCase().includes(searchLower) ||
        item.colors.toLowerCase().includes(searchLower)
      );
    }
  });

  const handleItemSelect = item => {
    setTempSelectedItems(prev => {
      const exists = prev.find(
        i =>
          selectedTab === 0
            ? i.id === item._id // Use _id for products
            : i.id === item.id, // Use id for materials
      );

      if (exists) {
        return prev.filter(i =>
          selectedTab === 0 ? i.id !== item._id : i.id !== item.id,
        );
      } else {
        const processedItem =
          selectedTab === 0
            ? {
                id: item._id,
                name: item.name,
                type: "product",
                brand: item.brand.name,
                category: item.primary_category,
                price: item.sellingPrice,
                unit: item.soldAs || "Unit",
                images: item.images,
                productId: item.productId,
              }
            : {
                id: item.id,
                name: item.name,
                type: "material",
                colors: item.colors,
                unit: item.unit,
                price: item.avgPrice,
                photo: item.photo,
              };
        return [...prev, processedItem];
      }
    });
  };

  const handleAddSelectedItems = () => {
    const newItems = tempSelectedItems.filter(
      item => !selectedItems.find(i => i.id === item.id),
    );

    setSelectedItems(prev => [...prev, ...newItems]);

    const newQuantities = { ...itemQuantities };
    newItems.forEach(item => {
      newQuantities[item.id] = 1;
    });
    setItemQuantities(newQuantities);

    setShowAddItemModal(false);
  };

  const handleRemoveItem = itemId => {
    setSelectedItems(selectedItems.filter(item => item.id !== itemId));
    const newQuantities = { ...itemQuantities };
    delete newQuantities[itemId];
    setItemQuantities(newQuantities);

    // Clean up material rows
    const newMaterialRows = { ...materialRows };
    delete newMaterialRows[itemId];
    setMaterialRows(newMaterialRows);
  };

  const handleQuantityChange = (itemId, value) => {
    if (value >= 0) {
      setItemQuantities({ ...itemQuantities, [itemId]: value });
    }
  };

  const handleAddColorRow = materialId => {
    setMaterialRows(prev => ({
      ...prev,
      [materialId]: [
        ...(prev[materialId] || []),
        { color: "", quantity: 1, price: 0, unit: "meters" },
      ],
    }));
  };

  const handleRowChange = (materialId, index, field, value) => {
    setMaterialRows(prev => ({
      ...prev,
      [materialId]: prev[materialId].map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleRemoveRow = (materialId, index) => {
    setMaterialRows(prev => ({
      ...prev,
      [materialId]: prev[materialId].filter((_, i) => i !== index),
    }));
  };

  const handleAddProductColorRow = productId => {
    setProductRows(prev => ({
      ...prev,
      [productId]: [
        ...(prev[productId] || []),
        { color: "", sizes: [{ size: "", quantity: 1, price: 0 }] },
      ],
    }));
  };

  const handleProductRowChange = (productId, colorIndex, field, value) => {
    setProductRows(prev => ({
      ...prev,
      [productId]: prev[productId].map((row, i) =>
        i === colorIndex ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleAddSize = (productId, colorIndex) => {
    setProductRows(prev => ({
      ...prev,
      [productId]: prev[productId].map((row, i) =>
        i === colorIndex
          ? {
              ...row,
              sizes: [...row.sizes, { size: "", quantity: 1, price: 0 }],
            }
          : row,
      ),
    }));
  };

  const handleSizeChange = (productId, colorIndex, sizeIndex, field, value) => {
    setProductRows(prev => ({
      ...prev,
      [productId]: prev[productId].map((row, i) =>
        i === colorIndex
          ? {
              ...row,
              sizes: row.sizes.map((size, si) =>
                si === sizeIndex ? { ...size, [field]: value } : size,
              ),
            }
          : row,
      ),
    }));
  };

  const handleRemoveProductRow = (productId, colorIndex) => {
    setProductRows(prev => ({
      ...prev,
      [productId]: prev[productId].filter((_, i) => i !== colorIndex),
    }));
  };

  const handleRemoveSize = (productId, colorIndex, sizeIndex) => {
    setProductRows(prev => ({
      ...prev,
      [productId]: prev[productId].map((row, i) =>
        i === colorIndex
          ? {
              ...row,
              sizes: row.sizes.filter((_, si) => si !== sizeIndex),
            }
          : row,
      ),
    }));
  };

  const handleAddColorClick = () => {
    setNewColor("");
    setColorError("");
    setShowAddColorModal(true);
  };

  const handleColorSubmit = () => {
    const trimmedColor = newColor.trim();
    if (!trimmedColor) {
      setColorError("Color name is required");
      return;
    }
    if (colors.includes(trimmedColor)) {
      setColorError("This color already exists");
      return;
    }
    setColors(prev => [...prev, trimmedColor]);
    setShowAddColorModal(false);
    setNewColor("");
  };

  const handleAddSizeClick = () => {
    setNewSize("");
    setSizeError("");
    setShowAddSizeModal(true);
  };

  const handleSizeSubmit = () => {
    const trimmedSize = newSize.trim();
    if (!trimmedSize) {
      setSizeError("Size name is required");
      return;
    }
    if (sizes.includes(trimmedSize)) {
      setSizeError("This size already exists");
      return;
    }
    setSizes(prev => [...prev, trimmedSize]);
    setShowAddSizeModal(false);
    setNewSize("");
  };

  const handleOpenImageModal = itemId => {
    setCurrentItemId(itemId);
    setShowImageModal(true);
  };

  const handleOpenNoteModal = itemId => {
    setCurrentItemId(itemId);
    setCurrentNote(itemNotes[itemId] || "");
    setShowNoteModal(true);
  };

  const handleSaveNote = () => {
    if (currentItemId) {
      setItemNotes(prev => ({
        ...prev,
        [currentItemId]: currentNote,
      }));
      setShowNoteModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold font-outfit text-gray-800">
              {editId
                ? `Edit Purchase Inward (${editId})`
                : "Create Purchase Inward"}
            </h1>
          </div>
        </div>
      </div>

      {/* Main content container */}
      <div className="container mx-auto px-6 py-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4 border-b pb-2">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Autocomplete
                options={suppliers}
                getOptionLabel={option => option.name || ""}
                value={suppliers.find(s => s.name === form.supplier) || null}
                onChange={(_, newValue) =>
                  handleChange("supplier", newValue?.name || "")
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select Supplier"
                    required
                    size="small"
                    error={Boolean(errors.supplier)}
                    helperText={errors.supplier}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Building2 size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                options={agents}
                getOptionLabel={option => option.name || ""}
                value={agents.find(a => a.name === form.agent) || null}
                onChange={(_, newValue) =>
                  handleChange("agent", newValue?.name || "")
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select Agent"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Wallet size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                options={purchaseOrders}
                getOptionLabel={option => option.id || ""}
                value={
                  purchaseOrders.find(po => po.id === form.purchaseOrderNo) ||
                  null
                }
                onChange={(_, newValue) =>
                  handleChange("purchaseOrderNo", newValue?.id || "")
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select Purchase Order"
                    required
                    size="small"
                    error={Boolean(errors.purchaseOrderNo)}
                    helperText={errors.purchaseOrderNo}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Receipt size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Challan Date"
                  value={form.challanDate}
                  onChange={newValue => handleChange("challanDate", newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: Boolean(errors.challanDate),
                      helperText: errors.challanDate,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                label="Challan No"
                size="small"
                required
                value={form.challanNo}
                onChange={e => handleChange("challanNo", e.target.value)}
                error={Boolean(errors.challanNo)}
                helperText={errors.challanNo}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Discount %"
                size="small"
                type="number"
                value={form.discount}
                onChange={e => handleChange("discount", e.target.value)}
                error={Boolean(errors.discount)}
                helperText={errors.discount}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className="text-[#5652B0]">%</span>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="L.R. No"
                size="small"
                value={form.lrNo}
                onChange={e => handleChange("lrNo", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Truck size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Transport Name"
                size="small"
                value={form.transport}
                onChange={e => handleChange("transport", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Truck size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                size="small"
                label="Add Note"
                multiline
                rows={1}
                placeholder="Add any additional notes..."
                value={form.note}
                onChange={e => handleChange("note", e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            {/* Challan Photos Section */}
            <div className="mt-6">
              <div className="flex items-center gap-4 mb-4">
                <h3 className="text-base font-medium text-gray-800">
                  Challan Photos
                </h3>
                <label className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] cursor-pointer flex items-center gap-2">
                  <ImageIcon size={16} />
                  Add Photo
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept="image/*"
                    onChange={e => handleImageUpload(null, e)}
                  />
                </label>
              </div>

              {form.challanPhotos.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {form.challanPhotos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <div className="w-32 h-32 rounded-lg border border-gray-200 overflow-hidden">
                        <img
                          src={photo}
                          alt={`Challan ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveImage(null, index)}
                        className="absolute -top-2 -right-2 bg-white shadow-sm border border-gray-200 text-gray-500 rounded-full p-1 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6 border-b pb-2">
              <h2 className="text-lg font-medium text-gray-800">Order Items</h2>
              <button
                className="px-6 py-2.5 rounded-lg text-base font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all flex items-center gap-2"
                onClick={handleAddItem}>
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            {selectedItems.length > 0 ? (
              <div className="space-y-4">
                {selectedItems.map(item => (
                  <div
                    key={item.id}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-3 border-b border-gray-100 bg-gray-50/50">
                      <div className="flex items-start gap-4">
                        <img
                          src={
                            item.type === "product"
                              ? "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                              : "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                          }
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex flex-col items-start">
                              <h3 className="text-lg font-medium text-gray-800">
                                {item.name}
                              </h3>
                              {item.type === "product" && (
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {item.brand} • {item.category}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleOpenNoteModal(item.id)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#5652B0] hover:bg-[#5652B0]/5 transition-all border border-[#5652B0]">
                                <FileText size={16} />
                                Add Note
                              </button>
                              <label
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#5652B0] hover:bg-[#5652B0]/5 transition-all border border-[#5652B0] cursor-pointer"
                                htmlFor={`image-upload-${item.id}`}>
                                <ImageIcon size={16} />
                                Add Image
                                <input
                                  type="file"
                                  id={`image-upload-${item.id}`}
                                  className="hidden"
                                  multiple
                                  accept="image/*"
                                  onChange={e => handleImageUpload(item.id, e)}
                                />
                              </label>
                              <button
                                onClick={() => handleRemoveItem(item.id)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-all border border-red-200">
                                <Trash2 size={16} />
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Note Display Section */}
                    {itemNotes[item.id] && (
                      <div className="px-4 py-3 border-t border-gray-100">
                        <div className="flex items-start gap-2">
                          <FileText
                            size={16}
                            className="text-gray-500 mt-0.5"
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-700 mb-1">
                              Note:
                            </div>
                            <div className="text-sm text-gray-600">
                              {itemNotes[item.id]}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image Preview Section */}
                    {itemImages[item.id] && itemImages[item.id].length > 0 && (
                      <div className="px-4 py-3 border-t border-gray-100">
                        <div className="flex flex-wrap gap-3">
                          {itemImages[item.id].map((image, index) => (
                            <div key={index} className="relative group">
                              <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden">
                                <img
                                  src={image}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <button
                                onClick={() =>
                                  handleRemoveImage(item.id, index)
                                }
                                className="absolute -top-1.5 -right-1.5 bg-white shadow-sm border border-gray-200 text-gray-500 rounded-full p-1 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="p-4">
                      {item.type === "product" ? (
                        <div className="space-y-4">
                          {(productRows[item.id] || []).map(
                            (colorRow, colorIndex) => (
                              <div
                                key={colorIndex}
                                className="rounded-lg border border-gray-100">
                                <div className="p-3 border-b border-gray-100 bg-white">
                                  <div className="flex items-center gap-4">
                                    <FormControl
                                      size="small"
                                      sx={{ width: 150 }}>
                                      <InputLabel>Color</InputLabel>
                                      <Select
                                        value={colorRow.color}
                                        label="Color"
                                        onChange={e =>
                                          handleProductRowChange(
                                            item.id,
                                            colorIndex,
                                            "color",
                                            e.target.value,
                                          )
                                        }>
                                        {colors.map(color => (
                                          <MenuItem key={color} value={color}>
                                            {color}
                                          </MenuItem>
                                        ))}
                                        <MenuItem
                                          onClick={e => {
                                            e.preventDefault();
                                            handleAddColorClick();
                                          }}
                                          sx={{
                                            color: "#5652B0",
                                            borderTop: "1px solid #e0e0e0",
                                            marginTop: 1,
                                            display: "flex",
                                            gap: 1,
                                          }}>
                                          <Plus size={16} />
                                          Add New Color
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                    <IconButton
                                      onClick={() =>
                                        handleRemoveProductRow(
                                          item.id,
                                          colorIndex,
                                        )
                                      }
                                      size="small"
                                      className="text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all">
                                      <X size={16} />
                                    </IconButton>
                                  </div>
                                </div>

                                <div className="p-3 space-y-2 bg-[#5652B0]/5">
                                  {colorRow.sizes.map((sizeRow, sizeIndex) => (
                                    <div
                                      key={sizeIndex}
                                      className="flex items-center gap-4">
                                      <FormControl
                                        size="small"
                                        sx={{ width: 150 }}>
                                        <InputLabel>Size</InputLabel>
                                        <Select
                                          value={sizeRow.size}
                                          label="Size"
                                          onChange={e =>
                                            handleSizeChange(
                                              item.id,
                                              colorIndex,
                                              sizeIndex,
                                              "size",
                                              e.target.value,
                                            )
                                          }>
                                          {sizes.map(size => (
                                            <MenuItem key={size} value={size}>
                                              {size}
                                            </MenuItem>
                                          ))}
                                          <MenuItem
                                            onClick={e => {
                                              e.preventDefault();
                                              handleAddSizeClick();
                                            }}
                                            sx={{
                                              color: "#5652B0",
                                              borderTop: "1px solid #e0e0e0",
                                              marginTop: 1,
                                              display: "flex",
                                              gap: 1,
                                            }}>
                                            <Plus size={16} />
                                            Add New Size
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                      <TextField
                                        type="number"
                                        size="small"
                                        label="Quantity"
                                        value={sizeRow.quantity}
                                        onChange={e =>
                                          handleSizeChange(
                                            item.id,
                                            colorIndex,
                                            sizeIndex,
                                            "quantity",
                                            parseInt(e.target.value),
                                          )
                                        }
                                        sx={{ width: 100 }}
                                      />
                                      <TextField
                                        type="number"
                                        size="small"
                                        label="Price"
                                        value={sizeRow.price}
                                        onChange={e =>
                                          handleSizeChange(
                                            item.id,
                                            colorIndex,
                                            sizeIndex,
                                            "price",
                                            parseFloat(e.target.value),
                                          )
                                        }
                                        sx={{ width: 100 }}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              ₹
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                      <div className="ml-auto flex items-center gap-4">
                                        <div className="text-right">
                                          <div className="text-sm text-gray-500">
                                            Subtotal
                                          </div>
                                          <div className="font-medium text-gray-800">
                                            ₹{sizeRow.quantity * sizeRow.price}
                                          </div>
                                        </div>
                                        <IconButton
                                          onClick={() =>
                                            handleRemoveSize(
                                              item.id,
                                              colorIndex,
                                              sizeIndex,
                                            )
                                          }
                                          size="small"
                                          className="text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all">
                                          <X size={16} />
                                        </IconButton>
                                      </div>
                                    </div>
                                  ))}

                                  <div className="flex">
                                    <button
                                      onClick={() =>
                                        handleAddSize(item.id, colorIndex)
                                      }
                                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                                      <Plus size={16} />
                                      Add Size
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                          <div className="flex">
                            <button
                              onClick={() => handleAddProductColorRow(item.id)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                              <Plus size={16} />
                              Add Color
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {(materialRows[item.id] || []).map((row, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-4 bg-white rounded-lg border border-gray-200 p-3">
                              <FormControl size="small" sx={{ width: 150 }}>
                                <InputLabel>Color</InputLabel>
                                <Select
                                  value={row.color}
                                  label="Color"
                                  onChange={e =>
                                    handleRowChange(
                                      item.id,
                                      index,
                                      "color",
                                      e.target.value,
                                    )
                                  }>
                                  {colors.map(color => (
                                    <MenuItem key={color} value={color}>
                                      {color}
                                    </MenuItem>
                                  ))}
                                  <MenuItem
                                    onClick={e => {
                                      e.preventDefault();
                                      handleAddColorClick();
                                    }}
                                    sx={{
                                      color: "#5652B0",
                                      borderTop: "1px solid #e0e0e0",
                                      marginTop: 1,
                                      display: "flex",
                                      gap: 1,
                                    }}>
                                    <Plus size={16} />
                                    Add New Color
                                  </MenuItem>
                                </Select>
                              </FormControl>
                              <FormControl size="small" sx={{ width: 120 }}>
                                <InputLabel>Unit</InputLabel>
                                <Select
                                  value={row.unit}
                                  label="Unit"
                                  onChange={e =>
                                    handleRowChange(
                                      item.id,
                                      index,
                                      "unit",
                                      e.target.value,
                                    )
                                  }>
                                  {units.map(unit => (
                                    <MenuItem key={unit} value={unit}>
                                      {unit}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <TextField
                                type="number"
                                size="small"
                                label="Quantity"
                                value={row.quantity}
                                onChange={e =>
                                  handleRowChange(
                                    item.id,
                                    index,
                                    "quantity",
                                    parseInt(e.target.value),
                                  )
                                }
                                sx={{ width: 100 }}
                              />
                              <TextField
                                type="number"
                                size="small"
                                label="Price"
                                value={row.price}
                                onChange={e =>
                                  handleRowChange(
                                    item.id,
                                    index,
                                    "price",
                                    parseFloat(e.target.value),
                                  )
                                }
                                sx={{ width: 100 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ₹
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              <div className="ml-auto flex items-center gap-4">
                                <div className="text-right">
                                  <div className="text-sm text-gray-500">
                                    Subtotal
                                  </div>
                                  <div className="font-medium text-gray-800">
                                    ₹{row.quantity * row.price}
                                  </div>
                                </div>
                                <button
                                  onClick={() =>
                                    handleRemoveRow(item.id, index)
                                  }
                                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors">
                                  <X size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                          <div className="flex">
                            <button
                              onClick={() => handleAddColorRow(item.id)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                              <Plus size={16} />
                              Add Color
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                      <div className="flex justify-end">
                        <div className="text-right">
                          <div className="text-sm text-gray-500">
                            Total Amount
                          </div>
                          <div className="text-lg font-medium text-gray-800">
                            ₹
                            {item.type === "product"
                              ? (productRows[item.id] || []).reduce(
                                  (colorSum, colorRow) =>
                                    colorSum +
                                    colorRow.sizes.reduce(
                                      (sizeSum, size) =>
                                        sizeSum + size.quantity * size.price,
                                      0,
                                    ),
                                  0,
                                )
                              : (materialRows[item.id] || []).reduce(
                                  (rowSum, row) =>
                                    rowSum + row.quantity * row.price,
                                  0,
                                )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end border-t pt-4 mt-4">
                  <div className="text-lg font-medium">
                    Total: ₹
                    {selectedItems.reduce((sum, item) => {
                      if (item.type === "product") {
                        return (
                          sum +
                          (productRows[item.id] || []).reduce(
                            (colorSum, colorRow) =>
                              colorSum +
                              colorRow.sizes.reduce(
                                (sizeSum, size) =>
                                  sizeSum + size.quantity * size.price,
                                0,
                              ),
                            0,
                          )
                        );
                      } else {
                        return (
                          sum +
                          (materialRows[item.id] || []).reduce(
                            (rowSum, row) => rowSum + row.quantity * row.price,
                            0,
                          )
                        );
                      }
                    }, 0)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No items added to the order yet
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6 flex justify-end space-x-4">
            <button
              onClick={handleCancel}
              className="px-6 py-2.5 rounded-lg text-base font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-lg text-base font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all flex items-center">
              {isSubmitting ? (
                <>
                  <CircularProgress
                    size={20}
                    color="inherit"
                    className="mr-2"
                  />
                  Saving...
                </>
              ) : (
                "Save Purchase Inward"
              )}
            </button>
          </div>
        </div>

        {/* Cancel Dialog */}
        <Dialog
          open={showCancelDialog}
          onClose={() => setShowCancelDialog(false)}
          aria-labelledby="cancel-dialog-title"
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle id="cancel-dialog-title">
            Cancel Purchase Inward?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel? All unsaved changes will be lost.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowCancelDialog(false)}
              sx={{
                color: "gray",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}>
              Keep Editing
            </Button>
            <Button
              onClick={handleCancelConfirmed}
              variant="contained"
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
              }}>
              Yes, Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Modal */}
        <Dialog
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            handleCancelConfirmed();
            navigate("/purchase/inward");
          }}
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle>Success!</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Purchase inward has been {editId ? "updated" : "created"}{" "}
              successfully.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                handleCancelConfirmed();
                navigate("/purchase/inward");
              }}
              variant="contained"
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
              }}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Item Modal */}
        <Dialog
          open={showAddItemModal}
          onClose={() => setShowAddItemModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle className="flex justify-between items-center">
            <span>Add Items</span>
            <IconButton
              onClick={() => setShowAddItemModal(false)}
              size="small"
              className="text-gray-500">
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div className="py-4">
              {/* Search and Tabs */}
              <div className="mb-6">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={20} className="text-gray-500" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    aria-label="item types"
                    sx={{
                      "& .MuiTab-root.Mui-selected": {
                        color: "#5652B0",
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#5652B0",
                      },
                    }}>
                    <Tab
                      icon={<Package2 size={18} />}
                      iconPosition="start"
                      label="Products"
                    />
                    <Tab
                      icon={<Layers size={18} />}
                      iconPosition="start"
                      label="Materials"
                    />
                  </Tabs>
                </Box>
              </div>

              {/* Items List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredItems.map(item => {
                  const isSelected = tempSelectedItems.some(i =>
                    selectedTab === 0 ? i.id === item._id : i.id === item.id,
                  );

                  return (
                    <div
                      key={selectedTab === 0 ? item._id : item.id}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? "border-[#5652B0] bg-purple-50"
                          : "border-gray-200 hover:border-[#5652B0]"
                      }`}
                      onClick={() => handleItemSelect(item)}>
                      <Checkbox
                        checked={isSelected}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                      {/* Image */}
                      <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0 mr-3">
                        {selectedTab === 0 && item.images?.length > 0 ? (
                          <img
                            src={item.images[0]}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : selectedTab === 1 && item.photo ? (
                          <img
                            src={item.photo}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ImageIcon size={24} />
                          </div>
                        )}
                      </div>
                      <div className="ml-2 flex-grow">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-gray-500">
                              {selectedTab === 0
                                ? `${item.brand.name} - ${item.productId}`
                                : `Colors: ${item.colors}`}
                            </p>
                          </div>
                          <p className="text-sm font-medium">
                            ₹{" "}
                            {selectedTab === 0
                              ? item.sellingPrice
                              : item.avgPrice}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowAddItemModal(false)}
              sx={{
                color: "gray",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedItems}
              variant="contained"
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
              }}>
              Add Selected ({tempSelectedItems.length})
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Modal */}
        <Dialog
          open={showImageModal}
          onClose={() => setShowImageModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle className="flex justify-between items-center">
            <span>Manage Images</span>
            <IconButton
              onClick={() => setShowImageModal(false)}
              size="small"
              className="text-gray-500">
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div className="py-4">
              <label className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] cursor-pointer inline-flex items-center gap-2 mb-4">
                <ImageIcon size={16} />
                Add Images
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/*"
                  onChange={e => handleImageUpload(currentItemId, e)}
                />
              </label>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
                {itemImages[currentItemId]?.map((image, index) => (
                  <div key={index} className="relative group">
                    <div className="w-full aspect-square rounded-lg border border-gray-200 overflow-hidden">
                      <img
                        src={image}
                        alt={`Image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveImage(currentItemId, index)}
                      className="absolute -top-2 -right-2 bg-white shadow-sm border border-gray-200 text-gray-500 rounded-full p-1 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Note Modal */}
        <Dialog
          open={showNoteModal}
          onClose={() => setShowNoteModal(false)}
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle>Add Note</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Note"
              fullWidth
              multiline
              rows={4}
              value={currentNote}
              onChange={e => setCurrentNote(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowNoteModal(false)}
              sx={{
                color: "gray",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNote}
              variant="contained"
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
              }}>
              Save Note
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Color Modal */}
        <Dialog
          open={showAddColorModal}
          onClose={() => setShowAddColorModal(false)}
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle>Add New Color</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Color Name"
              fullWidth
              value={newColor}
              onChange={e => setNewColor(e.target.value)}
              error={Boolean(colorError)}
              helperText={colorError}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowAddColorModal(false)}
              sx={{
                color: "gray",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleColorSubmit}
              variant="contained"
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
              }}>
              Add Color
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Size Modal */}
        <Dialog
          open={showAddSizeModal}
          onClose={() => setShowAddSizeModal(false)}
          PaperProps={{
            style: {
              borderRadius: "0.5rem",
            },
          }}>
          <DialogTitle>Add New Size</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Size Name"
              fullWidth
              value={newSize}
              onChange={e => setNewSize(e.target.value)}
              error={Boolean(sizeError)}
              helperText={sizeError}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowAddSizeModal(false)}
              sx={{
                color: "gray",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSizeSubmit}
              variant="contained"
              sx={{
                backgroundColor: "#5652B0",
                "&:hover": {
                  backgroundColor: "#4A47A3",
                },
              }}>
              Add Size
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default CreatePurchaseInward;
